import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'
import { useAnchoredPopover } from '@/shared/ui/popover'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  /** Доп. класс на подпись опции (напр. цветовая подсветка приоритета). */
  className?: string
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  // Показывается, когда value не совпал ни с одной опцией (пустой выбор).
  placeholder?: string
  disabled?: boolean
  id?: string
  className?: string
  // 'control' — полноразмерный контрол формы; 'inline' — «тихий» триггер-текст
  // (шапка проекта): без рамки/фона, аффорданс на hover, размер по содержимому.
  variant?: 'control' | 'inline'
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

const triggerControl =
  'flex w-full items-center gap-2 rounded-control border bg-bg-sunken px-4 pt-3 pb-[14px] text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:cursor-not-allowed disabled:opacity-50'

const triggerInline =
  '-mx-2 -my-0.5 inline-flex items-center gap-1 rounded-control px-2 py-0.5 text-left align-baseline transition-colors hover:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:cursor-not-allowed disabled:opacity-50'

// Кастомный select: триггер-combobox + listbox через anchored-popover (top-layer,
// не двигает layout внутри <dialog>; закрытие по Escape/клику вне, клавиатурная
// навигация, aria-activedescendant). Выбранная опция помечается акцентной галочкой,
// активная — приподнятым фоном — вместо серо-синего нативного хайлайта ОС,
// который не поддаётся стилизации.
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Выбрать…',
  disabled,
  id,
  className,
  variant = 'control',
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
}: SelectProps) {
  const uid = useId()
  const listId = `${uid}-list`
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const {
    anchorRef: triggerRef,
    popoverRef: listRef,
    popoverProps,
    // Inline-триггер узкий (размер по значению, вплоть до «—») — matchWidth
    // сжал бы листбокс до нечитаемого; для него ширину задаёт контент + min-width.
  } = useAnchoredPopover<HTMLButtonElement, HTMLDivElement>({
    open,
    matchWidth: variant === 'control',
  })

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Держим подсвеченную опцию в зоне видимости при навигации с клавиатуры.
  useEffect(() => {
    if (open && active >= 0) {
      listRef.current
        ?.querySelector<HTMLElement>(`#${uid}-opt-${active}`)
        ?.scrollIntoView({ block: 'nearest' })
    }
  }, [active, open, uid, listRef])

  function openMenu() {
    setActive(selectedIndex >= 0 ? selectedIndex : firstEnabled(options))
    setOpen(true)
  }

  function commit(i: number) {
    const opt = options[i]
    if (!opt || opt.disabled) return
    onChange(opt.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  function moveActive(dir: 1 | -1) {
    setActive((cur) => {
      let i = cur < 0 ? (dir === 1 ? -1 : 0) : cur
      for (let step = 0; step < options.length; step++) {
        i = (i + dir + options.length) % options.length
        if (!options[i]?.disabled) return i
      }
      return cur
    })
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (open) moveActive(1)
        else openMenu()
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open) moveActive(-1)
        else openMenu()
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (open) commit(active)
        else openMenu()
        break
      case 'Escape':
        if (open) {
          e.preventDefault()
          setOpen(false)
        }
        break
      case 'Home':
        if (open) {
          e.preventDefault()
          setActive(firstEnabled(options))
        }
        break
      case 'End':
        if (open) {
          e.preventDefault()
          setActive(lastEnabled(options))
        }
        break
    }
  }

  return (
    <div ref={rootRef} className={className}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${uid}-opt-${active}` : undefined}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedby}
        disabled={disabled}
        onClick={() => {
          if (open) setOpen(false)
          else openMenu()
        }}
        onKeyDown={onKeyDown}
        className={cn(
          variant === 'inline' ? triggerInline : triggerControl,
          variant === 'control' &&
            (ariaInvalid ? 'border-accent' : 'border-border-default focus:border-border-accent'),
        )}
      >
        <span
          className={cn(
            'min-w-0 truncate',
            variant === 'control' && 'flex-1',
            selected ? cn('text-text-primary', selected.className) : 'text-text-tertiary',
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon
          name="chevron-down"
          size={variant === 'inline' ? 14 : 16}
          className={cn('shrink-0 text-text-tertiary transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          ref={listRef}
          {...popoverProps}
          role="listbox"
          id={listId}
          className={cn(
            'm-0 max-h-64 overflow-auto rounded-card border border-border-default bg-bg-surface p-1 shadow-lg shadow-black/40',
            variant === 'inline' && 'min-w-40',
          )}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value
            const isActive = i === active
            return (
              <button
                key={opt.value}
                type="button"
                id={`${uid}-opt-${i}`}
                role="option"
                tabIndex={-1}
                aria-selected={isSelected}
                aria-disabled={opt.disabled}
                onMouseEnter={() => !opt.disabled && setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(i)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-control px-3 py-2 text-left text-sm transition-colors',
                  opt.disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
                  isActive ? 'bg-bg-raised text-text-primary' : 'text-text-secondary',
                )}
              >
                <span className={cn('min-w-0 flex-1 truncate', opt.className)}>{opt.label}</span>
                {isSelected && <Icon name="check" size={15} className="shrink-0 text-accent" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function firstEnabled(opts: SelectOption[]) {
  return opts.findIndex((o) => !o.disabled)
}
function lastEnabled(opts: SelectOption[]) {
  for (let i = opts.length - 1; i >= 0; i--) if (!opts[i]?.disabled) return i
  return -1
}
